import { addIconV2, editIconV2, trashDisableIcon, trashIconV2 } from '@/shared/ui/assets';
import { DOUBLE_HYPHEN_BULLET } from '@/shared/config';
import { AlertBanner, CustomButton, CustomTooltip } from '@/shared/ui/components';
import { gettextPatientImpact } from '@/shared/lib';
import { isFacilitySubscribeProtectPlus } from '@/entities/facility';
import { useRmaStore } from '@/entities/rma';
import dayjs from 'dayjs';
import _ from 'lodash';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import { ReasonRMA } from '../reasonRma';
import AddRMADeviceModal from './addRmaDeviceModal/addRmaDeviceModal';
import PatientImpactDrawer from './patientImpactDrawer/patientImpactDrawer';
import { useDeviceInformation } from './useDeviceInformation';
import QuestionMarkIcon from './assets/question-mark-circle.svg';
import type { DeviceInformationProps } from './index.types';
import './styles.scss';

const DeviceInformation = ({
  selectedFacility,
  onChangeDataDeviceRMA,
  isNotEdit,
  initialRmaDevices,
  hasReplaceDevice = false,
}: DeviceInformationProps) => {
  const maxDevicePerTicket = useRmaStore((s) => s.rmaLimitConfig.maxDevicePerTicket);
  const {
    state,
    listCurrentDeviceIds,
    listCurrentReplacementDeviceIds,
    handleRemoveDeviceIssue,
    handleUpdateDeviceIssue,
    toggleDisplayDrawer,
    closeAddRMADeviceModal,
    onClickAddMoreButton,
    getReplaceDeviceOptions,
    onClickEditButton,
  } = useDeviceInformation({ selectedFacility, onChangeDataDeviceRMA, initialRmaDevices });

  /**
   * Renders one editable device row of the new-RMA device table with device id, replacement device, RMA reasons, translated patient impact and the event date formatted as MM/DD/YYYY. Falls back to the first replacement option or the double-hyphen bullet for missing values, and appends delete and edit buttons where delete is disabled and drawn with the greyed trash icon while the ticket is not editable and only one device issue remains.
   *
   * @param item - Device issue row to render.
   * @param index - Zero-based row index, used as the key, displayed number and remove or edit target.
   * @returns The editable table row element for the device issue.
   */
  const renderDeviceIssues = (item: any, index: any) => {
    const replacedDeviceOptions = getReplaceDeviceOptions(item);
    return (
      <tr className="item-row" key={index}>
        <td className="number-id">{index + 1}</td>
        <td className="device-id">{item?.deviceId?.value || DOUBLE_HYPHEN_BULLET}</td>
        {hasReplaceDevice && (
          <td className="replacement-device-id">
            {item?.replacementDeviceId?.value ||
              replacedDeviceOptions[0]?.value ||
              DOUBLE_HYPHEN_BULLET}
          </td>
        )}
        <td className="rma-reasons">
          <ReasonRMA data={item} />
        </td>
        <td className="patient-impact">
          {gettextPatientImpact(item?.patientImpact?.label) || DOUBLE_HYPHEN_BULLET}
        </td>
        <td className="date-of-event">
          {item?.dateOfEvent ? dayjs(item?.dateOfEvent).format('MM/DD/YYYY') : ''}
          <CustomButton
            id={`delete-btn-${item?.deviceId?.value}`}
            icon={
              <ReactSVG
                src={
                  isNotEdit && state.deviceIssueArray.length < 2 ? trashDisableIcon : trashIconV2
                }
              />
            }
            disabled={isNotEdit && state.deviceIssueArray.length < 2}
            size="small"
            type="tertiary"
            style="neutral"
            className="btn-last btn-delete"
            onClick={() => {
              handleRemoveDeviceIssue(index);
            }}
            ariaLabel={t`Delete`}
          />
          <CustomButton
            id={`edit-btn-${item?.deviceId?.value}`}
            icon={<ReactSVG src={editIconV2} />}
            onClick={() => {
              onClickEditButton(item, index);
            }}
            size="small"
            type="tertiary"
            style="neutral"
            className="btn-last btn-edit"
            ariaLabel={t`Edit`}
          />
        </td>
      </tr>
    );
  };

  return (
    <>
      <PatientImpactDrawer
        open={state.isVisiblePatientImpactDrawer}
        title={t`Patient impact`}
        onCancel={toggleDisplayDrawer}
      />

      <AddRMADeviceModal
        deviceIssueArray={state.deviceIssueArray}
        open={state.isOpenAddRMADeviceModal}
        onCancel={closeAddRMADeviceModal}
        handleUpdate={handleUpdateDeviceIssue}
        index={state.deviceIssueIndex}
        listCurrentDeviceIds={listCurrentDeviceIds}
        listCurrentReplacementDeviceIds={listCurrentReplacementDeviceIds}
        initialDeviceIssue={state.initialDeviceIssue}
        facilityId={selectedFacility?.value}
        selectedFacility={selectedFacility}
        isNotEditRMA={isNotEdit}
        hasReplaceDevice={hasReplaceDevice}
        replacedDevices={state.replacedDevices}
        toggleDisplayDrawer={toggleDisplayDrawer}
        isLoading={state.isLoading}
        hasProtectPlus={isFacilitySubscribeProtectPlus(selectedFacility?.programs)}
      />

      <div className="device-issue">
        <div className="title">{t`RMA device information`}</div>
        {!isNotEdit && (
          <AlertBanner
            className="text-warning"
            alertType="warning"
            description={`Please note that you can only add a maximum of
                ${maxDevicePerTicket || DOUBLE_HYPHEN_BULLET}
              ${(maxDevicePerTicket ?? 0) > 1 ? 'devices' : 'device'}
              per ticket, except for 'Lost Device' reason.`}
          />
        )}
        <div className="table-device-wrapper" id="table-device-infor">
          <table className="table-device-issues" aria-label={t`RMA device information`}>
            <tr className="title-row">
              <th className="number-id">#</th>
              <th className="device-id">{t`Device ID`}</th>
              {hasReplaceDevice && (
                <th className="replacement-device-id">{t`Replacement device ID`}</th>
              )}
              <th className="device-issues">{t`Reason for RMA`}</th>
              <th className="patient-impact">
                {t`Patient impact`}
                <CustomTooltip
                  title={t`Learn more about patient impact`}
                  placement="bottom"
                  getPopupContainer={(ele: any) => ele.parentNode.parentNode}
                >
                  <span className="question-icon" onClick={toggleDisplayDrawer} aria-hidden="true">
                    <img src={QuestionMarkIcon} alt="Question mark icon" />
                  </span>
                </CustomTooltip>
              </th>
              <th className="date-of-event">{t`Date of event`}</th>
            </tr>
            {state.deviceIssueArray?.length ? (
              _.map(state.deviceIssueArray, (item, index) => renderDeviceIssues(item, index))
            ) : (
              <tr className="item-row item-row__empty">
                <div className="no-data" {...({ colSpan: '100%' } as any)}>
                  {t`There is no device yet, click “Add more device” to add one.`}
                </div>
              </tr>
            )}
          </table>
        </div>
        {!isNotEdit && (
          <div className="btn-add-device">
            <CustomButton
              id="add-more-btn"
              className="add-more-button"
              onClick={onClickAddMoreButton}
              icon={<ReactSVG src={addIconV2} />}
              type="secondary"
            >
              {t`Add more device`}
            </CustomButton>
          </div>
        )}
      </div>
    </>
  );
};

export default DeviceInformation;
